<template>
    <div class="d-flex flex-column h-100">
        <div class="flex-grow-0">
            <slot name="header"></slot>
        </div>
        <div v-if="isLoading" class="text-muted text-center mt-5 font-18">
            Loading...
        </div>
        <template v-else>
            <div class="flex-grow-1">
                <div class="automation-editor-container">
                    <AutomationEditor
                        :type="type"
                        :recipe="recipe"
                        @change="onRecipeChange"
                        class="automation-editor"
                        ref="automation-editor"/>
                </div>
            </div>
            <div class="flex-grow-0">
                <div class="d-flex align-items-center p-3">
                    <div class="flex-grow-1 text-right">
                        <slot name="before-save-btn"></slot>
                        <button @click="save"
                                class="btn btn-primary mt-0"
                                :disabled="!isValidRecipe || isSaving">
                            {{ isCreation ? 'Create' : 'Save' }}
                        </button>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import AutomationEditor from '../../../components/automation/AutomationEditor'
import {checkIsValidAutomationRecipe} from '@/types/automations/utils'
import {getRecipeInvalidAuthParamPaths} from './hooks/auth'
import {ConnectionStatuses} from '@/types/apps/connections'
import {AutomationBuilderTabs} from './index'
import {automationsApi} from '@/api/automations'
import {AppDocSystemTypes} from '@/types/document/types'
import {mapGetters} from 'vuex'
import {showErrorMessage} from '@/services/snackbar'

export default {
    name: 'AutomationBuilder',
    components: {AutomationEditor},
    props: {
        automationId: {
            type: String
        },
        isTemplate: { // if template => create a new board automation, else => edit existing one
            type: Boolean,
            default: false
        },
        evalOrder: {
            type: Number,
            default: 0
        }
    },
    data() {
        return {
            automation: {
                categories: []
            },
            recipe: null,
            authParam: {
                path: null,
                value: null
            },
            isLoading: false,
            isSaving: false,
            AutomationBuilderTabs
        }
    },
    computed: {
        ...mapGetters([
            'userUUID'
        ]),

        /**
         *  NOTE: currently automation type restricted to one value
         */
        typeModel() {
            return _.get(this.automation.types, 0)
        },

        type() {
            return _.get(this.typeModel, 'uuid')
        },

        isCustomNew() {
            return !this.automation.uuid && (
                !this.automation.categories || this.automation.categories.length === 0
            )
        },

        isCreation() {
            return this.isCustomNew || this.isTemplate
        },

        isValidRecipe() {
            return checkIsValidAutomationRecipe(this.recipe)
        },

        hasAllAuthParamsSet() {
            return this.invalidAuthParamPaths.length === 0
        },

        invalidAuthParamPaths() {
            return getRecipeInvalidAuthParamPaths(this.recipe)
        }
    },
    methods: {
        onRecipeChange({path, data}) {
            const recipe = _.cloneDeep(this.recipe)
            _.set(recipe, path, data)
            this.recipe = recipe
        },

        getRecipePartNameByAuthParamPath(path) {
            const type = _.get(path, 0)
            if (type === 'trigger') {
                return _.get(this.recipe, 'trigger.name')
            } else if (type === 'actions') {
                return _.get(this.recipe, ['actions', _.get(path, 1), 'name'])
            } else if (type === 'condition') {
                return _.get(this.recipe, ['conditions', _.get(path, 1), 'name'])
            }
            return 'Unknown'
        },

        async save() {
            this.isSaving = true
            const data = {
                ...this.automation,
                recipe: this.recipe,
                isTemplate: false,
                enabled: this.isCreation ? true : this.automation.enabled
            }
            if (this.isTemplate) { // creation from template automation
                delete data.uuid
            }
            let error = null
            let _axios = null
            if (data.uuid) {
                ({error, _axios} = await automationsApi.update({id: data.uuid, data}))
            } else {
                data.evalOrder = this.evalOrder;
                ({error, _axios} = await automationsApi.add({
                    section: AppDocSystemTypes.PURCHASING,
                    user: this.userUUID,
                    data
                }))
            }

            if (error) {
                const messages = _.get(_axios, 'error.response.data.errors', [])
                const message = messages && messages.length > 0
                    ? messages.join('. ')
                    : 'Failed to save automation'
                showErrorMessage(message, {duration: 10000, withClose: true})
            } else {
                this.$emit('refresh')
                this.$emit('close')
            }
            this.isSaving = false
        },

        onAuthParamUpdate(param) {
            this.authParam.value = param.value
        },

        checkIsValidConnection(connection) {
            return connection && connection.status === ConnectionStatuses.CONNECTED
        },

        async setAutomationUrl() {
            if (!this.automation.uuid) {
                return
            }
            const query = {
                ...this.$route.query,
                automationType: this.type,
                automationId: this.automation.uuid
            }
            await this.$router.replace({query}).catch(() => {
            })
        },

        async clearAutomationUrl() {
            const query = {...this.$route.query}
            delete query.automationId
            await this.$router.replace({query}).catch(() => {
            })
        },

        async fetchAutomation() {
            if (!this.automationId) {
                return
            }
            const {error, data} = await automationsApi.fetch(this.automationId)
            if (error) {
                showErrorMessage('Failed to fetch automation')
            } else {
                this.automation = data
            }
        }
    },
    async created() {
        this.isLoading = true
        await this.fetchAutomation()
        if (this.isCustomNew) {
            this.recipe = {changeable: true}
        } else {
            this.recipe = _.isPlainObject(_.get(this.automation, 'recipe')) ? this.automation.recipe : {}
        }
        this.setAutomationUrl()
        this.isLoading = false
    },
    beforeDestroy() {
        this.clearAutomationUrl()
    }
}
</script>

<style lang="scss" scoped>
.automation-editor-container {
    display: flex;
    justify-content: center;
    align-items: center;
    align-self: center;
    text-align: center;
    height: 100%;
    width: 100%;
    padding: 0 40px;

    .automation-editor {
        position: relative;
        width: 70%;
        max-width: 900px;
        min-width: 360px;
    }

    .auth-container {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 20px;
    }
}
</style>
